/**
 * Appointments API client. Currently routes to the mock adapter (no backend
 * yet). When the real `/api/v1/appointments` lands, flip the flag in
 * `mock/registry.ts` or per-environment via `VITE_USE_MOCKS=false`.
 */
import { apiClient } from "./client";
import { MOCKS_ENABLED } from "./mock/registry";
import { mockAppointments } from "./mock/appointments";
import type {
  AppointmentCreateRequest,
  AppointmentDetail,
  AppointmentListFilter,
  AppointmentRescheduleRequest,
  AppointmentStatus,
  AppointmentSummary,
} from "../types/appointment";
import type { PageResponse } from "../types/patient";
import { randomUuid } from "../randomUuid";
import { isoDate, shiftIsoDate } from "../format";

/**
 * Body for the reception assign action (frozen backend contract):
 * POST /api/v1/appointments/{id}/assign → updated appointment
 * (sets assignState=ASSIGNED once the required slot is filled).
 */
export interface AppointmentAssignRequest {
  doctorProfileId?: string;
  roomId?: string;
}

/**
 * Body for the full-edit action ("Sửa lịch hẹn" — Redmine #18507):
 * PUT /api/v1/appointments/{id} → updated appointment summary.
 * Changing `roomId` allocates a new room number automatically (keeps the old
 * room's number). A `startAt` in the past → 422 code `appointment_in_past`.
 */
export interface AppointmentEditRequest {
  startAt: string;
  durationMinutes: number;
  doctorProfileId: string | null;
  departmentId: string | null;
  serviceId: string | null;
  roomId: string | null;
  notes: string | null;
}

/**
 * Full edit of an appointment's "Thông tin lịch" (Redmine #18507).
 * Real PUT /api/v1/appointments/{id} — always hits the backend (no mock;
 * mirrors the standalone `assign` helper's style). 422 `appointment_in_past`
 * when `startAt` is in the past.
 */
export function editAppointment(
  appointmentId: string,
  body: AppointmentEditRequest
): Promise<AppointmentSummary> {
  return apiClient
    .put<AppointmentSummary>(`/v1/appointments/${appointmentId}`, body)
    .then((r) => r.data);
}

/**
 * Assign a doctor and/or room to a pending appointment ("Phân công").
 * Real /api/v1/appointments/{id}/assign — always hits the backend
 * (the assign flow has no mock; contract is frozen).
 */
export function assign(
  appointmentId: string,
  body: AppointmentAssignRequest
): Promise<AppointmentDetail> {
  return apiClient
    .post<AppointmentDetail>(`/v1/appointments/${appointmentId}/assign`, {
      doctorProfileId: body.doctorProfileId ?? null,
      roomId: body.roomId ?? null,
    })
    .then((r) => r.data);
}

/** A fresh Idempotency-Key per booking submit so a network-level resend can't double-book. */
function idem() {
  return { headers: { "Idempotency-Key": randomUuid() } };
}

// Real client placeholder — wires to apiClient once `/api/v1/appointments`
// is implemented. Kept here so the call sites already use the right names.
const realClient = {
  list(
    filter: AppointmentListFilter | undefined,
    pageable: { page?: number; size?: number; sort?: string }
  ): Promise<PageResponse<AppointmentSummary>> {
    const params: Record<string, string | number> = {};
    if (filter?.q) params.q = filter.q;
    if (filter?.status) params.status = filter.status;
    if (filter?.patientId) params.patientId = filter.patientId;
    if (filter?.doctorId) params.doctorId = filter.doctorId;
    if (filter?.fromDate) params.fromDate = filter.fromDate;
    if (filter?.toDate) params.toDate = filter.toDate;
    if (pageable.page != null) params.page = pageable.page;
    if (pageable.size != null) params.size = pageable.size;
    return apiClient
      .get<PageResponse<AppointmentSummary>>("/v1/appointments", { params })
      .then((r) => r.data);
  },
  forDate(date: string): Promise<AppointmentSummary[]> {
    return apiClient
      .get<PageResponse<AppointmentSummary>>("/v1/appointments", {
        params: { fromDate: date, toDate: date, size: 200 },
      })
      .then((r) => r.data.content);
  },
  /**
   * Mọi lịch hẹn của tuần bắt đầu `weekStart` (thứ Hai, giờ địa phương), sắp theo giờ hẹn (#19861).
   *
   * Hai lỗi cũ, một lần sửa: ngày được tính bằng `isoDate` / `shiftIsoDate` theo GIỜ ĐỊA PHƯƠNG
   * (`toISOString()` của 00:00 thứ Hai ở +07 là 17:00 Chủ nhật tuần TRƯỚC, nên Chủ nhật của tuần
   * đang xem không bao giờ được tải), và endpoint `/appointments/week` không phân trang (danh sách
   * cũ bị server chặn ở 200 dòng, sắp giảm dần — tuần đông mất những lịch sớm nhất).
   */
  forWeek(weekStart: Date): Promise<AppointmentSummary[]> {
    return apiClient
      .get<AppointmentSummary[]>("/v1/appointments/week", {
        params: { fromDate: isoDate(weekStart), toDate: shiftIsoDate(weekStart, 6) },
      })
      .then((r) => r.data);
  },
  get(id: string): Promise<AppointmentDetail> {
    return apiClient
      .get<AppointmentDetail>(`/v1/appointments/${id}`)
      .then((r) => r.data);
  },
  create(req: AppointmentCreateRequest): Promise<AppointmentDetail> {
    return apiClient
      .post<AppointmentSummary>("/v1/appointments", req, idem())
      .then((r) => ({
        ...r.data,
        patientCode: null,
        patientDateOfBirth: null,
        createdAt: r.data.startAt,
        updatedAt: r.data.startAt,
        events: [],
      }));
  },
  transition(id: string, to: AppointmentStatus, note?: string): Promise<void> {
    return apiClient
      .post<void>(`/v1/appointments/${id}/transition`, { to, note: note ?? null })
      .then(() => undefined);
  },
  assign(id: string, body: AppointmentAssignRequest): Promise<AppointmentDetail> {
    return assign(id, body);
  },
  reschedule(id: string, req: AppointmentRescheduleRequest): Promise<void> {
    return apiClient
      .post(`/v1/appointments/${id}/reschedule`, req)
      .then(() => undefined);
  },
  /** Hard-delete an appointment. Frozen contract: 204. */
  remove(id: string): Promise<void> {
    return apiClient
      .delete(`/v1/appointments/${id}/permanent`)
      .then(() => undefined);
  },
  listDoctors() {
    return apiClient
      .get<{ id: string; name: string; specialty: string | null }[]>(
        "/v1/doctors/options"
      )
      .then((r) => r.data);
  },
  listServices() {
    return apiClient
      .get<
        { id: string; name: string; durationMinutes: number; priceVnd: number }[]
      >("/v1/services")
      .then((r) => r.data);
  },
  searchPatients(q: string) {
    if (!q || q.trim().length < 1) return Promise.resolve([]);
    return apiClient
      .get<
        {
          id: string;
          fullName: string;
          patientCode: string | null;
          phoneE164: string | null;
          dateOfBirth: string | null;
        }[]
      >("/v1/patients/lookup", { params: { q } })
      .then((r) =>
        r.data.map((p) => ({
          id: p.id,
          name: p.fullName,
          code: p.patientCode ?? "",
          phone: p.phoneE164,
          dob: p.dateOfBirth,
        }))
      );
  },
};

export const appointmentsApi = MOCKS_ENABLED ? mockAppointments : realClient;

export const appointmentQueryKeys = {
  all: ["appointments"] as const,
  list: (filter: AppointmentListFilter | undefined, page: number) =>
    ["appointments", "list", filter ?? {}, page] as const,
  forDate: (date: string) => ["appointments", "date", date] as const,
  forWeek: (weekStart: string) => ["appointments", "week", weekStart] as const,
  detail: (id: string) => ["appointments", "detail", id] as const,
  doctors: () => ["doctors-options"] as const,
  services: () => ["services-options"] as const,
};
